import {
  duplicateRecipients,
  isBareEmailAddress,
  MAX_RECIPIENTS,
  subjectProblem,
} from '~/cynqueModules/emailRecipientRules'
import type { IEntitySelectItem } from '~/types/models'
import type {
  IAddressedEmailForPost,
  IEmailMessage,
} from '~/types/email/models'

// The mock's half of an email posted inside an entity's request -- an
// opportunity registration's `registrationEmail` -- as opposed to a case
// reply, whose handler (api/msw/api/cases) checks its own body.
//
// The keys are the registration endpoint's (cynque-api-server #1765,
// `_COMPOSED_KEYS`): the stored message's spelling, toAddresses / ccAddresses /
// bccAddresses, and any other key a 400 -- `to` included, which is what the
// webapp sent until 2026-10-27. The address rules are the case reply's
// (#1666), which the server applies to every email it sends.

const EMAIL_FOR_POST_KEYS: readonly string[] = [
  'toAddresses',
  'ccAddresses',
  'bccAddresses',
  'subject',
  'body',
  'documentIds',
  'sendToSuppressed',
] satisfies readonly (keyof IAddressedEmailForPost)[]

/**
 * Why the server would refuse this email, or null. `toAddresses` is required: an email
 * posted with an entity has no thread to fall back on for a recipient
 * (IAddressedEmailForPost).
 */
export function emailForPostProblem(email: unknown): string | null {
  if (!email || typeof email !== 'object' || Array.isArray(email)) {
    return 'The email is not an object.'
  }
  const unknownKeys = Object.keys(email).filter(
    (key) => !EMAIL_FOR_POST_KEYS.includes(key)
  )
  if (unknownKeys.length) {
    return `Unknown email field(s): ${unknownKeys.join(', ')}.`
  }
  const {
    toAddresses: to,
    ccAddresses: cc = [],
    bccAddresses: bcc = [],
    subject,
    body,
  } = email as IAddressedEmailForPost
  if (typeof body !== 'string') {
    return 'body is required.'
  }
  if (!to?.length) {
    return 'toAddresses must name at least one address.'
  }
  const badAddress = [...to, ...cc, ...bcc].find(
    (address) => !isBareEmailAddress(address)
  )
  if (badAddress !== undefined) {
    return `'${badAddress}' is not a bare email address (local@domain).`
  }
  const repeated = duplicateRecipients(to, cc, bcc)
  if (repeated.length) {
    return `Each recipient may appear once across to, cc and bcc: ${repeated.join(
      ', '
    )}.`
  }
  const total = to.length + cc.length + bcc.length
  if (total > MAX_RECIPIENTS) {
    return `${total} recipients; at most ${MAX_RECIPIENTS}.`
  }
  if (subject !== undefined) {
    const problem = subjectProblem(subject)
    if (problem !== true) {
      return problem
    }
  }
  return null
}

/**
 * The message a posted email becomes, in `queued`.
 *
 * ⚠ It STAYS queued, as a case reply's does (appendOutboundReplyEmail):
 * nothing in the mock sends, and a mock that advanced it to `sent` would be
 * making a promise the server does not make.
 */
export function queuedEmailMessage({
  id,
  email,
  subjectFallback,
  sentBy,
}: {
  id: string
  email: IAddressedEmailForPost
  // The owner's default subject -- a registration's `descr`.
  subjectFallback: string
  sentBy: IEntitySelectItem
}): IEmailMessage {
  const orNull = (addresses: string[] | undefined) =>
    addresses?.length ? addresses : null
  return {
    id,
    direction: 'outbound',
    body: email.body,
    sentBy,
    repeatOf: null,
    deliveryState: 'queued',
    subject: email.subject ?? subjectFallback,
    fromAddress: 'sales@wxtechnologygroup.com',
    fromName: 'WX Technology Group',
    toAddresses: orNull(email.toAddresses),
    ccAddresses: orNull(email.ccAddresses),
    bccAddresses: orNull(email.bccAddresses),
    replyTo: null,
    sentAt: null,
    receivedAt: null,
    createdAt: new Date().toISOString(),
    messageId: null,
    inReplyTo: null,
    messageReferences: null,
    providerMessageId: null,
    mailboxHash: null,
    spamScore: null,
  }
}
